import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';
import napcoLogo from '../assets/napco-logo.png';
import napcoLogoCollapsed from '../assets/napco-logo-collapsed.png';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import { logout } from '../lib/auth';
import { 
  Home, 
  BarChart2, 
  Settings, 
  Users, 
  FileText, 
  HelpCircle,
  Menu,
  X,
  LogOut,
  Search
} from 'lucide-react';
import { getShipments } from '../lib/utils';

// All navigation items with access control information
const navItems = [
  { 
    name: 'Dashboard', 
    icon: Home, 
    path: '/', 
    // accessible to everyone
  },
  // { name: 'Analytics', icon: BarChart2, path: '/analytics' },
  { 
    name: 'Reports', 
    icon: FileText, 
    path: '/reports',
    access: (userData) => userData?.role !== 'Forwarder'
  },
  { 
    name: 'Users', 
    icon: Users, 
    path: '/user-management', 
    access: (userData) => userData?.role === 'Admin'
  },
  // { name: 'Settings', icon: Settings, path: '/settings' },
  { 
    name: 'Support', 
    icon: HelpCircle, 
    path: 'https://cloudbase.freshdesk.com/a/tickets/new',
    // accessible to everyone
  },
];

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  // State to track if the search input is focused
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  // State to hold the search input value
  const [searchValue, setSearchValue] = useState('');
  
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userData } = useUserData();

  // Redirect from unauthorized routes
  useEffect(() => {
    if (userData) {
      console.log('Sidebar: userData state:', userData);
      const currentPath = location.pathname;
      const currentNavItem = navItems.find(item => item.path === currentPath);
      
      if (currentNavItem && currentNavItem.access && !currentNavItem.access(userData)) {
        navigate('/');
      }
    }
  }, [location.pathname, userData, navigate]);

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle hover events
  const handleMouseEnter = () => {
    setIsCollapsed(false);
  };

  // Only collapse if search input is not focused
  const handleMouseLeave = () => {
    if (!isSearchFocused) {
      setIsCollapsed(true);
    }
  };

  // For mobile, we'll keep the manual toggle
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Function to check if a nav item should be visible
  const isNavItemVisible = (item) => {
    if (!item.access) return true; // If no access function, item is visible to all
    return userData && item.access(userData);
  };

  // Handle search input key press events
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchValue) {
      navigate(`/shipment-details/${searchValue}`);
    }
  };

  // Update the useEffect to only fetch suggestions when searchValue length is exactly 4
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchValue.length >= 4) {
        setIsLoading(true);
        try {
          const response = await getShipments(`?${new URLSearchParams({ shipmentMatch: searchValue }).toString()}`);
          if (response.data && response.data.shipments) {
            setSuggestions(response.data.shipments);
          }
        } catch (error) {
          console.error("Error fetching shipment suggestions:", error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      } else if (searchValue.length < 4) {
        setSuggestions([]);
      }
      // If searchValue.length > 4, we keep the existing suggestions and filter them client-side
    };

    fetchSuggestions();
  }, [searchValue]);

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isMobile && !isCollapsed && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-20"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <motion.div
        className={cn(
          "fixed top-0 left-0 h-full bg-white shadow-lg z-30",
          "flex flex-col transition-all duration-300 ease-in-out",
          isCollapsed ? "w-20" : "w-64",
          "md:relative md:translate-x-0",
          !isCollapsed && "translate-x-0",
          isCollapsed && "-translate-x-full md:translate-x-0"
        )}
        initial={{ x: -100 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3 }}
        onMouseEnter={!isMobile ? handleMouseEnter : undefined}
        onMouseLeave={!isMobile ? handleMouseLeave : undefined}
      >
        {/* Sidebar header */}
        <div className={cn(
          "flex items-center border-b border-gray-200",
          isCollapsed ? "justify-center p-2" : "justify-between p-4"
        )}>
          {!isCollapsed ? (
            <motion.div
              className="flex items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src={napcoLogo} 
                alt="NAPCO Logo" 
                className="h-8 object-contain"
              />
            </motion.div>
          ) : (
            <motion.div
              className="flex items-center justify-center w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src={napcoLogoCollapsed} 
                alt="NAPCO Logo" 
                className="h-12 w-12 object-contain"
              />
            </motion.div>
          )}
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <Menu size={20} /> : <X size={20} />}
            </button>
          )}
        </div>

        {/* Search Bar (visible only when sidebar is open) */}
        {!isCollapsed && (
          <div className="p-4 border-b border-gray-200 relative">
            
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
                <Search size={18} className={`text-gray-400`} />
              </div>
              <input
                id="search-shipments"
                type="text"
                placeholder="Search shipments"
                className={cn(
                  "w-full p-2 pl-8 border border-gray-300 rounded-md",
                  isSearchFocused && "outline outline-2 outline-red-500"
                )}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
            </div>
            {/* Dropdown suggestions */}
            {searchValue.length >= 4 && (
              <ul className="z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                {isLoading ? (
                  <li className="px-2 py-1 text-center text-gray-600">Loading...</li>
                ) : suggestions.length > 0 ? (
                  suggestions
                    .filter((item) => 
                      item.toLowerCase().includes(searchValue.toLowerCase())
                    )
                    .map((item) => (
                      <li
                        key={item}
                        className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                        onMouseDown={() => {
                          setSearchValue(item);
                          navigate(`/shipment-details/${item}`);
                        }}
                      >
                        {item}
                      </li>
                    ))
                ) : (
                  <li className="px-2 py-1 text-center text-gray-600">No matches found</li>
                )}
              </ul>
            )}
          </div>
        )}

        {/* Navigation links */}
        <nav className={cn(
          "flex-1 py-4",
          isCollapsed ? "overflow-hidden" : "overflow-y-auto transition-all duration-300 ease-in-out",
          !isCollapsed && "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400",
          // Dim the nav sections when search is active
          isSearchFocused && "opacity-50"
        )}>
          <ul className={cn(
            "space-y-4", 
            isCollapsed ? "px-1" : "px-2"
          )}>
            {navItems.map((item) => {
              // Skip items user doesn't have access to
              if (!isNavItemVisible(item)) {
                return null;
              }
              
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              const isExternalLink = item.path.startsWith('http');
              
              return (
                <li key={item.name}>
                  {isExternalLink ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "flex items-center rounded-md transition-colors duration-200",
                        isCollapsed ? "justify-center py-3 px-2" : "p-3", 
                        "hover:bg-gray-100",
                        "text-gray-700"
                      )}
                    >
                      <Icon 
                        size={isCollapsed ? 24 : 20} 
                        className="flex-shrink-0 text-gray-500" 
                      />
                      {!isCollapsed && (
                        <span className="ml-3 font-medium">{item.name}</span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={cn(
                        "flex items-center rounded-md transition-colors duration-200",
                        isCollapsed ? "justify-center py-3 px-2" : "p-3", 
                        "hover:bg-gray-100",
                        isActive 
                          ? "bg-accent-50 text-accent-600" 
                          : "text-gray-700"
                      )}
                    >
                      <Icon 
                        size={isCollapsed ? 24 : 20} 
                        className={cn(
                          "flex-shrink-0",
                          isActive ? "text-accent-600" : "text-gray-500"
                        )} 
                      />
                      {!isCollapsed && (
                        <span className="ml-3 font-medium">{item.name}</span>
                      )}
                      {isActive && !isCollapsed && (
                        <motion.div
                          className="absolute left-0 w-1 h-8 bg-accent-600 rounded-r-md"
                          layoutId="activeIndicator"
                          transition={{ duration: 0.2 }}
                        />
                      )}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info and logout */}
        <div className="p-4 border-t border-gray-200">
          {!isCollapsed && currentUser && (
            <div className="mb-3 px-2 text-sm text-gray-600">
              <p className="truncate">Signed in as:</p>
              <p className="font-medium truncate">{currentUser.email}</p>
              {userData && userData.role && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800 border border-accent-200">
                    {userData.role}
                  </span>
                </div>
              )}
            </div>
          )}
          {isCollapsed && currentUser && userData && userData.role && (
            <div className="flex justify-center mb-3">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800 border border-accent-200">
                {userData.role.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={cn(
              "flex items-center rounded-md transition-colors duration-200",
              "text-gray-700 hover:bg-gray-100",
              isCollapsed ? "justify-center py-3 px-2 w-full" : "p-3 w-full" 
            )}
          >
            <LogOut 
              size={isCollapsed ? 24 : 20} 
              className="text-gray-500" 
            />
            {!isCollapsed && <span className="ml-3 font-medium">Logout</span>}
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
